import React, { useState, useEffect } from "react";
import {
  Table,
  Button,
  Modal,
  Form,
  Pagination,
  Spinner,
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { BsTrash } from "react-icons/bs";
import { FaEdit } from "react-icons/fa";
import { useFindEvents } from "../../api/queries/useFindEvents";
import { useSaveEvent } from "../../api/mutations/useSaveEvent";
import { Event } from "../../types/Types";
import { useDeleteEvent } from "../../api/mutations/useDeleteEvent";

const ManageEvents: React.FC = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState<Event | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [eventToDelete, setEventToDelete] = useState<Event | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  const navigate = useNavigate();

  // Reset pagination when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm]);

  // Debounce search term
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchQuery);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [searchQuery]);

  const { data: events, refetch } = useFindEvents({
    pageSize,
    page: currentPage,
    searchTerm: debouncedSearchTerm || undefined,
  });

  const { mutate: updateEvent, isPending } = useSaveEvent({
    onSuccess: () => {
      setModalOpen(false);
      refetch();
    },
  });

  const { mutate: deleteEvent, isPending: isDeleting } = useDeleteEvent({
    onSuccess: () => {
      setShowDeleteModal(false);
      setEventToDelete(null);
      refetch();
    },
  });

  const handleDelete = (event: Event) => {
    setEventToDelete(event);
    setShowDeleteModal(true);
  };

  const handleEdit = (event: Event) => {
    setEditingEvent(event);
    setModalOpen(true);
  };

  const handleCreate = () => {
    setEditingEvent(null);
    setModalOpen(true);
  };

  const handleSave = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const newEvent: Partial<Event> = {
      id: editingEvent?.id,
      name: (form.elements.namedItem("name") as HTMLInputElement)?.value || "",
      description: form.description.value,
      price: parseFloat(form.price.value),
      earlyBirdPrice: parseFloat(form.earlyBirdPrice.value),
      seats: parseInt(form.numberAvailable.value, 10),
      earlyBirdDateEnd: (form.elements.namedItem("date") as HTMLInputElement)
        ?.value,
      eventDate: (form.elements.namedItem("eventDate") as HTMLInputElement)
        ?.value,
      location:
        (form.elements.namedItem("location") as HTMLInputElement)?.value || "",
      productId:
        (form.elements.namedItem("productId") as HTMLInputElement)?.value || "",
    };

    updateEvent(newEvent);
  };

  const confirmDelete = () => {
    if (eventToDelete) {
      deleteEvent(eventToDelete.id);
    }
  };

  // Calculate total pages for pagination
  const totalPages = Math.ceil((events?.totalRecords ?? 0) / pageSize);

  // Handle page change for pagination
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    refetch();
  };

  return (
    <div className="container mt-2">
      <div className="row">
        <div className="col-md-9 mb-4">
          <Form.Control
            type="text"
            placeholder="Search events..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-100 w-md-50"
          />
        </div>
        <div className="col-md-3">
          <Button
            className="btn-bg w-100 w-md-auto mb-3"
            onClick={handleCreate}
          >
            + Create Event
          </Button>
        </div>
      </div>

      {/* Events Table */}
      <Table striped bordered hover responsive>
        <thead className="table-primary">
          <tr>
            <th className="align-top">Name</th>
            <th className="align-top">Description</th>
            <th className="align-top">City</th>
            <th className="align-top">Price</th>
            <th className="align-top">Event Date</th>
            <th className="align-top">Early Bird (EB)</th>
            <th className="align-top">EB End Date</th>
            <th className="align-top">Total Available</th>
            <th className="align-top">Actions</th>
          </tr>
        </thead>
        <tbody>
          {events?.data.map((event) => (
              <tr key={event.id}>
                <td
                  className="pointer-hover-underline"
                  onClick={() => navigate(`/admin/event/${event.id}`)}
                >
                  {event.name}
                </td>
                <td>{event.description}</td>
                <td>{event.location}</td>
                <td>£{event.price}</td>
                <td>{new Date(event.eventDate).toLocaleDateString()}</td>
                <td>£{event.earlyBirdPrice}</td>
                <td>{new Date(event.earlyBirdDateEnd).toLocaleDateString()}</td>
                <td>{event.seats}</td>
                <td>
                  <Button
                    size="sm"
                    variant="link"
                    onClick={() => handleEdit(event)}
                    className="me-2 text-secondary"
                  >
                    <FaEdit />
                  </Button>
                  <Button
                    size="sm"
                    variant="link"
                    onClick={() => handleDelete(event)}
                    className="text-danger"
                  >
                    <BsTrash />
                  </Button>
                </td>
              </tr>
            ))}
        </tbody>
      </Table>

      {/* Pagination Controls */}
      {events?.totalRecords && events?.totalRecords > 10 && (
        <div className="d-flex justify-content-center mt-4">
          <Pagination>
            <Pagination.First
              onClick={() => handlePageChange(1)}
              disabled={currentPage === 1}
            />
            <Pagination.Prev
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
            />
            {[...Array(totalPages)].map((_, i) => (
              <Pagination.Item
                key={i + 1}
                active={i + 1 === currentPage}
                onClick={() => handlePageChange(i + 1)}
              >
                {i + 1}
              </Pagination.Item>
            ))}
            <Pagination.Next
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
            />
            <Pagination.Last
              onClick={() => handlePageChange(totalPages)}
              disabled={currentPage === totalPages}
            />
          </Pagination>
        </div>
      )}

      {/* Event Create/Edit Modal */}
      <Modal
        size="lg"
        centered
        show={modalOpen}
        onHide={() => setModalOpen(false)}
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {editingEvent ? "Edit Event" : "Create Event"}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSave}>
          <Modal.Body className="d-flex flex-column gap-3">
            <Form.Group>
              <Form.Label>Name</Form.Label>
              <Form.Control
                name="name"
                defaultValue={editingEvent?.name || ""}
                required
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Description</Form.Label>
              <Form.Control
                name="description"
                defaultValue={editingEvent?.description || ""}
                required
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>City</Form.Label>
              <Form.Control
                name="location"
                defaultValue={editingEvent?.location || ""}
                required
              />
            </Form.Group>
            <div className="d-flex flex-wrap gap-3">
              <Form.Group>
                <Form.Label>Price</Form.Label>
                <Form.Control
                  name="price"
                  type="number"
                  defaultValue={editingEvent?.price || 0}
                  required
                />
              </Form.Group>
              <Form.Group>
                <Form.Label>Early Bird Discount</Form.Label>
                <Form.Control
                  name="earlyBirdPrice"
                  type="number"
                  defaultValue={editingEvent?.earlyBirdPrice || 0}
                  required
                />
              </Form.Group>
              <Form.Group>
                <Form.Label>Available</Form.Label>
                <Form.Control
                  name="numberAvailable"
                  type="number"
                  defaultValue={editingEvent?.seats || 0}
                  required
                />
              </Form.Group>
            </div>
            <div>
              <Form.Group>
                <Form.Label>Product Id</Form.Label>
                <Form.Control
                  name="productId"
                  defaultValue={editingEvent?.productId || ""}
                  required
                />
              </Form.Group>
            </div>
            <div>
              <Form.Group>
                <Form.Label>Event Date</Form.Label>
                <Form.Control
                  name="eventDate"
                  type="date"
                  defaultValue={
                    editingEvent?.eventDate
                      ? new Date(editingEvent.eventDate)
                          .toISOString()
                          .split("T")[0]
                      : ""
                  }
                  required
                />
              </Form.Group>
            </div>
            <div>
              <Form.Group>
                <Form.Label>Early Bird End Date</Form.Label>
                <Form.Control
                  name="date"
                  type="date"
                  defaultValue={
                    editingEvent?.earlyBirdDateEnd
                      ? new Date(editingEvent.earlyBirdDateEnd)
                          .toISOString()
                          .split("T")[0]
                      : ""
                  }
                  required
                />
              </Form.Group>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setModalOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={isPending}>
              {isPending ? "Saving..." : "Save"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        centered
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm Deletion</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete <strong>{eventToDelete?.name}</strong>
          ?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button
            disabled={isDeleting}
            variant="danger"
            onClick={confirmDelete}
          >
            {isDeleting ? "Deleting..." : "Delete"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ManageEvents;
